import { createTheme } from "@material-ui/core/styles";
import type { Overrides } from "@material-ui/core/styles/overrides";
import { PALETTE } from "./palette";

export type Font = {
  fontFamily: string;
  src: string;
  fontWeight: "normal";
  fontStyle: "normal";
  fontSmoothing: "antialiased";
};

const spaceUnit = 8;
const defaultTheme = createTheme();

export const generateOverrides = ({ fonts }: { fonts: Font[] }) =>
  ({
    MuiCssBaseline: {
      "@global": {
        "@font-face": fonts,
      },
    },
    MuiFormControl: {
      root: {
        // MUI v4 hardcodes `z-index: 1` on the floating InputLabel (a Chrome
        // autofill workaround — see InputLabel `filled`/`outlined` styles).
        // Because FormControl is `position: relative` without a z-index it is
        // NOT a stacking context, so that label z-index escapes to the root and
        // the label text bleeds on top of overlays, sticky bars, modals and
        // dropdowns. `isolation: isolate` makes each field its own stacking
        // context, scoping the label's z-index to the field and fixing the
        // bleed globally without affecting layout. See mui/material-ui#18333.
        isolation: "isolate",
      },
    },
    MuiIconButton: {
      root: {
        // disableRipple removes ButtonBase's focus ripple, but MUI still adds
        // the JS-driven `.Mui-focusVisible` class on keyboard focus only (works
        // on old WebViews, unlike the CSS `:focus-visible`). Keying off it
        // instead of `:focus` stops the background sticking after a mouse click.
        "&.Mui-focusVisible": {
          backgroundColor: defaultTheme.palette.action.focus,
        },
      },
    },
    MuiButton: {
      root: {
        height: 44,
        borderRadius: 200, // High value so corners are always fully rounded
        minWidth: 182,
        padding: "12px 24px",
        fontFamily: "NotoSans Bold",
        fontWeight: 700,
        "&:not(:hover)": {
          // Only show shadows on hover
          boxShadow: "none",
        },
      },
      sizeLarge: {
        height: 56,
      },
    },
    MuiTab: {
      root: {
        borderBottom: `2px solid ${PALETTE.grey[300]}`,
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: spaceUnit,
      },
    },
    MuiCheckbox: {
      colorPrimary: {
        // color: "red",
      },
      root: {
        transition: defaultTheme.transitions.create(["color"], {
          duration: defaultTheme.transitions.duration.standard,
        }),
      },
    },
    MuiOutlinedInput: {
      root: {
        backgroundColor: PALETTE.grey[200],
        borderRadius: spaceUnit,
        padding: "8px 16px",
        outline: "0 solid transparent",
        transition: defaultTheme.transitions.create(
          ["background-color", "outline-color", "outline-width"],
          { duration: defaultTheme.transitions.duration.shortest },
        ),
        "&$focused": {
          outline: `2px solid ${PALETTE.primary.main}`,
          backgroundColor: PALETTE.background.paper,
        },
        "&$error": {
          outline: `2px solid ${PALETTE.error.main}`,
        },
      },
      inputMarginDense: {
        paddingTop: 16,
      },
      inputMultiline: {
        paddingTop: 6,
      },
      notchedOutline: {
        border: "0 none",
      },
      input: {
        padding: "16px 0 8px",
        caretColor: PALETTE.primary.main,
      },
    },
    MuiFormLabel: {
      root: {
        marginLeft: 16,
        "&$disabled": {
          color: PALETTE.grey[300],
        },
      },
    },
    MuiInputLabel: {
      outlined: {
        color: "#222222",
        transform: "translate(0, 22px) scale(1)",
        "&$shrink": {
          transform: "translate(0, 8px) scale(0.75)",
        },
      },
      asterisk: {
        color: PALETTE.primary.main,
      },
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: "transparent",
        },
      },
      icon: {
        color: PALETTE.primary.main,
        transitionProperty: "transform",
        transitionTimingFunction: "ease-out",
        transitionDuration: "300ms",
      },
    },
    MuiAccordion: {
      rounded: {
        "&:first-child": {
          borderTopLeftRadius: spaceUnit,
          borderTopRightRadius: spaceUnit,
        },

        "&:last-child": {
          borderBottomLeftRadius: spaceUnit,
          borderBottomRightRadius: spaceUnit,
        },
      },
    },
  }) satisfies Overrides;
